// =============================================================
//  Impresión de tickets térmicos (58 mm) desde el navegador
// -------------------------------------------------------------
//  Replica los tickets de la app móvil (ParkingAndroidKotlin,
//  funciones/MetodosImpresion.kt) pero usando el driver de
//  Windows: se arma el comprobante en HTML/CSS dentro de un
//  iframe oculto y se manda a window.print().
//
//  Por qué así y no ESC/POS como en el móvil: el navegador no
//  puede abrir un socket Bluetooth SPP/RFCOMM ni un puerto raw.
//  Web Bluetooth solo habla BLE/GATT, que estas impresoras no
//  exponen. Con el driver instalado en Windows el navegador sí
//  imprime, y además podemos usar QR e imagen sin comandos.
//
//  Ancho: una impresora de 58 mm imprime 384 puntos a 203 dpi,
//  es decir 48 mm reales. El papel es de 58 mm, así que la
//  página se declara de 58 mm y el contenido se centra en 48.
// =============================================================

import qrcode from "qrcode-generator"

/** Ancho imprimible real en mm (384 puntos / 203 dpi). */
const ANCHO_CONTENIDO_MM = 48

/** Escapa texto para que no rompa el HTML del comprobante. */
const esc = (valor) =>
  String(valor ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

/**
 * Normaliza una placa al estándar del proyecto: solo A-Z y 0-9, en
 * mayúsculas y SIN guion, igual que normalizarPlaca() del móvil.
 * El backend busca membresías por coincidencia exacta, así que web,
 * móvil y backend tienen que guardar el mismo formato.
 */
export const normalizarPlaca = (valor) =>
  String(valor ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")

/**
 * Parsea las fechas del backend recortando los microsegundos a
 * milisegundos (llegan como 2026-09-10T09:43:15.123456).
 */
export const parseFechaBackend = (valor) => {
  if (!valor) return null
  const recortada = String(valor).replace(/(\.\d{3})\d+$/, "$1")
  const fecha = new Date(recortada)
  return isNaN(fecha.getTime()) ? null : fecha
}

const dosDigitos = (n) => String(n).padStart(2, "0")

/** yyyy-MM-dd, igual que el móvil. */
export const fmtFecha = (fecha) =>
  `${fecha.getFullYear()}-${dosDigitos(fecha.getMonth() + 1)}-${dosDigitos(fecha.getDate())}`

/** HH:mm:ss, igual que el móvil (toHHmmss). */
export const fmtHora = (fecha) =>
  `${dosDigitos(fecha.getHours())}:${dosDigitos(fecha.getMinutes())}:${dosDigitos(fecha.getSeconds())}`

/**
 * Sello de fecha/hora para el ticket: usa la hora del backend si llegó
 * (es la oficial, la pone la BD) y si no cae a la hora local.
 */
export const sellarFechaHora = (valorBackend) => {
  const fecha = parseFechaBackend(valorBackend) || new Date()
  return { fecha: fmtFecha(fecha), hora: fmtHora(fecha) }
}

const money = (valor) => (valor == null || isNaN(Number(valor)) ? "0.00" : Number(valor).toFixed(2))

/** QR en SVG inline: no hay que esperar a que cargue ninguna imagen. */
const qrSvg = (texto) => {
  const qr = qrcode(0, "M")
  qr.addData(String(texto))
  qr.make()
  return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true })
}

const ESTILOS = `
  @page { size: 58mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    width: ${ANCHO_CONTENIDO_MM}mm;
    margin: 0 auto;
    padding: 2mm 0 8mm;
    color: #000;
    font-family: "Courier New", ui-monospace, monospace;
    font-size: 11px;
    line-height: 1.3;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .centro { text-align: center; }
  .sucursal { font-size: 15px; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase; }
  .marca { font-weight: 700; letter-spacing: 0.5px; }
  .titulo { font-size: 16px; font-weight: 700; letter-spacing: 1px; margin: 1mm 0; }
  .sep { border-top: 1px dashed #000; margin: 1.6mm 0; }
  .dato { display: flex; gap: 2mm; }
  .dato b { font-weight: 700; }
  .total-label { font-weight: 700; letter-spacing: 0.5px; margin-top: 1mm; }
  .total-monto { font-size: 26px; font-weight: 700; letter-spacing: 1px; margin: 0.5mm 0 1mm; }
  .qr { width: 26mm; margin: 2.5mm auto 1mm; }
  .qr svg { display: block; width: 100%; height: auto; shape-rendering: crispEdges; }
  .info { font-size: 9px; line-height: 1.35; margin-top: 1.5mm; white-space: pre-line; }
  .pie { font-weight: 700; letter-spacing: 0.5px; margin-top: 1mm; }
`

/** Cabecera común: nombre de sucursal, marca y contacto. */
const cabecera = (sucursal) => `
  <div class="centro sucursal">${esc(sucursal?.nombre)}</div>
  <div class="centro marca">PARKING CLUB</div>
  ${sucursal?.ubicacion ? `<div class="centro">${esc(sucursal.ubicacion)}</div>` : ""}
`

/**
 * Manda un comprobante a la impresora. Usa un iframe oculto para no
 * tocar la página de la app: lo que se imprime es solo el ticket.
 * Resuelve cuando el navegador termina (o cancela) el diálogo.
 */
const imprimirHtml = (titulo, cuerpo) =>
  new Promise((resolve) => {
    const documento = `<!doctype html><html><head><meta charset="utf-8">
      <title>${esc(titulo)}</title><style>${ESTILOS}</style></head>
      <body>${cuerpo}</body></html>`

    const iframe = document.createElement("iframe")
    iframe.setAttribute("aria-hidden", "true")
    iframe.setAttribute("title", titulo)
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;"

    let terminado = false
    const limpiar = () => {
      if (terminado) return
      terminado = true
      clearTimeout(reserva)
      // Un pelo de espera: quitar el iframe demasiado pronto aborta el envío.
      setTimeout(() => iframe.remove(), 500)
      resolve(true)
    }
    // Red de seguridad si el navegador nunca dispara afterprint.
    const reserva = setTimeout(limpiar, 60000)

    iframe.onload = () => {
      const ventana = iframe.contentWindow
      if (!ventana) {
        limpiar()
        return
      }
      ventana.addEventListener("afterprint", limpiar, { once: true })
      ventana.focus()
      ventana.print()
    }

    iframe.srcdoc = documento
    document.body.appendChild(iframe)
  })

/**
 * Ticket de INGRESO. Mismo contenido que printTicket() del móvil:
 * cabecera, título, placa/fecha/hora, QR con la placa y la
 * descripción configurada en la sucursal.
 */
export const imprimirTicketIngreso = ({ sucursal, placa, fecha, hora }) => {
  const cuerpo = `
    ${cabecera(sucursal)}
    <div class="sep"></div>
    <div class="centro titulo">TICKET DE INGRESO</div>
    <div class="sep"></div>
    <div class="dato"><b>Placa:</b> ${esc(placa)}</div>
    <div class="dato"><b>Fecha:</b> ${esc(fecha)}</div>
    <div class="dato"><b>Hora de ingreso:</b> ${esc(hora)}</div>
    <div class="qr">${qrSvg(placa)}</div>
    ${sucursal?.descripcionTicket ? `<div class="centro info">${esc(sucursal.descripcionTicket)}</div>` : ""}
  `
  return imprimirHtml(`Ticket de ingreso ${placa}`, cuerpo)
}

/**
 * Ticket de SALIDA. Mismo contenido que printSalida() del móvil:
 * cabecera, título, placa/fecha/entrada/salida, total y pie.
 */
export const imprimirTicketSalida = ({ sucursal, placa, fecha, horaEntrada, horaSalida, monto }) => {
  const cuerpo = `
    ${cabecera(sucursal)}
    <div class="sep"></div>
    <div class="centro titulo">TICKET DE SALIDA</div>
    <div class="sep"></div>
    <div class="dato"><b>Placa:</b> ${esc(placa)}</div>
    <div class="dato"><b>Fecha:</b> ${esc(fecha)}</div>
    <div class="dato"><b>Entrada:</b> ${esc(horaEntrada)}</div>
    <div class="dato"><b>Salida:</b> ${esc(horaSalida)}</div>
    <div class="sep"></div>
    <div class="total-label">TOTAL CANCELADO:</div>
    <div class="total-monto">$ ${money(monto)}</div>
    <div class="sep"></div>
    <div class="centro pie">¡GRACIAS POR SU VISITA!</div>
  `
  return imprimirHtml(`Ticket de salida ${placa}`, cuerpo)
}
